// Rendu et recalcul des tableaux de breakdowns analytiques (Alloc. actifs,
// Géographie actions, Capitalisation, Devises), partagés entre le Compositeur
// et le Réplicateur. Logique extraite de arbitrage_ia_compositeur_controller.
//
// Un "fund entry" représente une ligne d'inventaire :
//   { targetWeight: Number, breakdowns: { asset: {}, equity_country: {}, ... } }

function number(value) {
  const parsed = parseFloat((value || "0").toString().replace(",", "."))
  return isNaN(parsed) ? 0 : parsed
}

function formatPercent(value) {
  return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}

const TOTAL_TOLERANCE = 0.05

function totalIsBalanced(value) {
  return Math.abs(number(value) - 100) <= TOTAL_TOLERANCE
}

function formatTotalPercent(value) {
  const numeric = number(value)
  return formatPercent(totalIsBalanced(numeric) ? 100 : numeric)
}

function formatSigned(value) {
  const sign = value > 0 ? "+" : "-"
  return `${sign}${Math.abs(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}

function deltaClass(value) {
  if (value > 0.01) return "is-up"
  if (value < -0.01) return "is-down"
  return "is-neutral"
}

function totalDeltaText(value) {
  return Math.abs(number(value)) <= TOTAL_TOLERANCE ? "–" : formatSigned(value)
}

function totalDeltaClass(value) {
  return Math.abs(number(value)) <= TOTAL_TOLERANCE ? "is-neutral" : deltaClass(value)
}

function escape(value) {
  const element = document.createElement("div")
  element.textContent = value || ""
  return element.innerHTML
}

function safeColor(value) {
  return /^#[0-9a-f]{6}$/i.test(value || "") ? value : "#5B8F7B"
}

function breakdownTargetSlug(config, slug) {
  if (!config.consolidateCountries) return slug
  if (slug === "emerging_europe") return "europe"
  if (slug === "frontiers") return "emerging"
  return slug
}

function base100(values) {
  const total = Object.keys(values).reduce((sum, key) => sum + number(values[key]), 0)
  if (Math.abs(total) < 0.000001) return values

  return Object.keys(values).reduce((normalized, key) => {
    normalized[key] = (number(values[key]) / total) * 100
    return normalized
  }, {})
}

function breakdownRow(config, breakdown) {
  const value = number(breakdown.y)
  const color = safeColor(breakdown.color)
  const formattedValue = value > 0.005 ? formatPercent(value) : "–"

  return `
    <div class="aia-eos-row" data-breakdown-row data-breakdown-kind="${config.kind}" data-breakdown-slug="${escape(breakdown.slug)}" data-breakdown-before="${value}" data-breakdown-target-value="${value}">
      <i data-accent style="background-color:${color}"></i>
      <span class="aia-eos-row__main">
        <strong>${escape(breakdown.name)}</strong>
      </span>
      <span class="aia-num">${formattedValue}</span>
      <span class="aia-num" data-breakdown-target>${formattedValue}</span>
      <span class="aia-num is-neutral" data-breakdown-delta>–</span>
      <span class="aia-visual-bar">
        <b data-breakdown-bar style="width:${Math.min(Math.max(value, 0), 100)}%; background-color:${color}"></b>
      </span>
    </div>
  `
}

function breakdownTotalRow(breakdowns) {
  const beforeTotal = breakdowns.reduce((sum, breakdown) => sum + number(breakdown.y), 0)
  const formattedTotal = formatTotalPercent(beforeTotal)

  return `
    <div class="aia-eos-total aia-eos-total--breakdown" data-breakdown-total>
      <span>Total</span>
      <strong data-breakdown-before-total>${formattedTotal}</strong>
      <strong data-breakdown-target-total>${formattedTotal}</strong>
      <strong class="aia-num is-neutral" data-breakdown-delta-total>–</strong>
      <b data-breakdown-total-state>Équilibré</b>
    </div>
  `
}

function syncBreakdownTotal(frame) {
  const totalRow = frame.querySelector("[data-breakdown-total]")
  if (!totalRow) return

  const rows = Array.from(frame.querySelectorAll("[data-breakdown-row]"))
  const beforeTotal = rows.reduce((sum, row) => sum + number(row.dataset.breakdownBefore), 0)
  const targetTotal = rows.reduce((sum, row) => sum + number(row.dataset.breakdownTargetValue), 0)
  const delta = targetTotal - beforeTotal
  const beforeElement = totalRow.querySelector("[data-breakdown-before-total]")
  const targetElement = totalRow.querySelector("[data-breakdown-target-total]")
  const deltaElement = totalRow.querySelector("[data-breakdown-delta-total]")
  const stateElement = totalRow.querySelector("[data-breakdown-total-state]")

  beforeElement.textContent = formatTotalPercent(beforeTotal)
  targetElement.textContent = formatTotalPercent(targetTotal)
  deltaElement.textContent = totalDeltaText(delta)
  deltaElement.className = `aia-num ${totalDeltaClass(delta)}`
  stateElement.textContent = Math.abs(delta) <= TOTAL_TOLERANCE ? "Équilibré" : "À ajuster"
  stateElement.classList.toggle("is-error", Math.abs(delta) > TOTAL_TOLERANCE)
}

export function fetchBreakdown(url) {
  return fetch(url, {
    method: "GET",
    headers: { "Accept": "application/json" }
  }).then((response) => {
    if (!response.ok) throw new Error("analytical_breakdown_error")
    return response.json()
  })
}

export function renderBreakdownTable(frame, config, data) {
  const breakdowns = Array.isArray(data) ? data : []
  const total = breakdowns.reduce((sum, breakdown) => sum + number(breakdown.y), 0)

  if (total <= 0.005) {
    frame.innerHTML = `<div class="aia-empty-state">${config.emptyMessage}</div>`
    return breakdowns
  }

  frame.innerHTML = `
    <div class="aia-eos-table aia-eos-table--breakdown">
      <div class="aia-eos-table__head">
        <span>${config.title}</span>
        <span>Avant</span>
        <span>Cible</span>
        <span>Delta</span>
        <span>Cible visuelle</span>
      </div>
      ${breakdowns.map((breakdown) => breakdownRow(config, breakdown)).join("")}
      ${breakdownTotalRow(breakdowns)}
    </div>
  `
  return breakdowns
}

export function syncBreakdownFrame(frame, config, fundEntries) {
  if (!frame) return

  const targets = {}

  fundEntries.forEach((entry) => {
    const rowBreakdown = (entry.breakdowns || {})[config.dataKey] || {}
    const assetBreakdown = (entry.breakdowns || {}).asset || {}
    const multiplier = config.assetMultiplier ? number(assetBreakdown[config.assetMultiplier]) : 1

    Object.keys(rowBreakdown).forEach((slug) => {
      const targetSlug = breakdownTargetSlug(config, slug)
      targets[targetSlug] = (targets[targetSlug] || 0) + entry.targetWeight * number(rowBreakdown[slug]) * multiplier
    })
  })

  const normalizedTargets = base100(targets)

  frame.querySelectorAll("[data-breakdown-row]").forEach((row) => {
    const slug = row.dataset.breakdownSlug
    const before = number(row.dataset.breakdownBefore)
    const target = normalizedTargets[slug] || 0
    const delta = target - before
    const targetElement = row.querySelector("[data-breakdown-target]")
    const deltaElement = row.querySelector("[data-breakdown-delta]")
    const bar = row.querySelector("[data-breakdown-bar]")

    row.dataset.breakdownTargetValue = target
    targetElement.textContent = target > 0.005 ? formatPercent(target) : "–"
    deltaElement.textContent = Math.abs(delta) < 0.01 ? "–" : formatSigned(delta)
    deltaElement.className = `aia-num ${deltaClass(delta)}`
    row.classList.toggle("is-up", delta > 0.01)
    row.classList.toggle("is-down", delta < -0.01)
    bar.style.width = `${Math.min(Math.max(target, 0), 100)}%`
  })

  syncBreakdownTotal(frame)
}
